import { ReactNode, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { CONNECTORS_PAGE } from '../../../../lib/use-connectors';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useTranslation } from '../../../../lib/i18n';
import { Button, Card } from '../../ui';
import styles from './mobile.module.css';

/**
 * A channel "connect an app" card, mobile (Figma 13131-79232/79284): a grey icon tile + the client
 * name, a hairline, the how-to instructions, then a full-width Copy → Copied button. The copied value
 * is produced by `getValue` — a static endpoint for most clients, or an async act-as-me token mint for
 * Claude Code — and cached so repeated copies don't mint again. Unlike the desktop `ConnectAppCard`
 * the literal value is never shown on the card; it goes straight to the clipboard.
 */
export function ConnectInstructionCard({
  icon,
  title,
  description,
  getValue,
  copyBiName,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  getValue: () => Promise<string> | string;
  copyBiName: string;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const cached = useRef<string | null>(null);

  const onCopy = async () => {
    logPageClickBi({ ...CONNECTORS_PAGE, button_name: copyBiName });
    setBusy(true);
    try {
      const value = cached.current ?? (await getValue());
      cached.current = value;
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Mint or clipboard write failed — there's nothing shown to fall back to on the mobile card,
      // so leave the button as-is; tapping again retries.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="sunken" size="sm" className={styles.connectCard}>
      <div className={styles.connectHead}>
        <span className={styles.connectTile} aria-hidden>
          {icon}
        </span>
        <div className={styles.rowName}>{title}</div>
      </div>
      <hr className={styles.connectDivider} aria-hidden />
      <p className={styles.connectDesc}>{description}</p>
      <Button
        fullWidth
        size="large"
        variant="outlined"
        loading={busy}
        startIcon={copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
        onClick={onCopy}
      >
        {copied ? t('connections.connect.copied') : t('connections.connect.copy')}
      </Button>
    </Card>
  );
}
